import '@/pages/admin/admin.css';

import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';
import { LoadingContainer } from '@/components/ui';
import { sysadminService, type AsesoriaSinActaDto } from '@/services/sysadmin.service';
import { getAsesoriaEstadoLabel } from '@/utils/asesoriaStatus';
import { AlertTriangle, ClipboardX, FileWarning } from 'lucide-react';
import { useEffect, useState } from 'react';

// ─── Column definitions (compartidas por las 2 tablas) ─────────────

type ColKey = 'asesoriaId' | 'empresaNombre' | 'asesorNombre' | 'estado' | 'fechaInicio' | 'horas';

const COLS: ColDef<ColKey>[] = [
  { key: 'asesoriaId', label: 'ID', defaultVisible: true },
  { key: 'empresaNombre', label: 'Empresa', defaultVisible: true },
  { key: 'asesorNombre', label: 'Asesor', defaultVisible: true },
  { key: 'estado', label: 'Estado', defaultVisible: true },
  { key: 'fechaInicio', label: 'Inicio', defaultVisible: true },
  { key: 'horas', label: 'Horas', defaultVisible: false },
];

function formatDate(d?: string | null) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

// ─── Tabla reutilizable con columnas colapsables ────────────────────

function ActasTable({
  storageKey,
  asesorias,
  emptyMessage,
  onGenerarActa,
  generandoId,
}: {
  storageKey: string;
  asesorias: AsesoriaSinActaDto[];
  emptyMessage: string;
  onGenerarActa?: (asesoriaId: number) => void;
  generandoId?: number | null;
}) {
  const { visible, toggle, reset, activeCount } = useColumnVisibility<ColKey>(COLS, storageKey);
  const visibleCols = COLS.filter((c) => visible[c.key]);

  return (
    <div className="admin-card">
      <div className="admin-card-header flex items-center justify-between gap-3">
        <div className="text-[13px] font-semibold text-(--admin-text) flex items-center gap-2">
          <span className="bg-(--admin-surface2) px-1.5 py-0.5 rounded text-(--admin-text3) font-normal text-[11px] border border-(--admin-border)">
            {asesorias.length}
          </span>
        </div>
        <ColumnPicker cols={COLS} visible={visible} onToggle={toggle} onReset={reset} activeCount={activeCount} />
      </div>
      <div className="overflow-x-auto">
        <table className="admin-table">
          <thead>
            <tr>
              {visibleCols.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
              {onGenerarActa && <th></th>}
            </tr>
          </thead>
          <tbody>
            {asesorias.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length + (onGenerarActa ? 1 : 0)} className="text-center py-10 text-(--admin-text3)">
                  <ClipboardX className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  <div className="text-[13px]">{emptyMessage}</div>
                </td>
              </tr>
            ) : (
              asesorias.map((a) => (
                <tr key={a.asesoriaId}>
                  {visible.asesoriaId && (
                    <td className="text-(--admin-text2) font-mono" style={{ textAlign: 'right' }}>
                      {a.asesoriaId}
                    </td>
                  )}
                  {visible.empresaNombre && <td className="text-(--admin-text) font-medium">{a.empresaNombre}</td>}
                  {visible.asesorNombre && <td className="text-(--admin-text2)">{a.asesorNombre}</td>}
                  {visible.estado && (
                    <td>
                      <span className="admin-chip text-[10px] bg-(--admin-purple-bg) text-(--admin-purple)">
                        {getAsesoriaEstadoLabel(a.estado as any)}
                      </span>
                    </td>
                  )}
                  {visible.fechaInicio && <td className="text-(--admin-text2) whitespace-nowrap">{formatDate(a.fechaInicio)}</td>}
                  {visible.horas && (
                    <td className="text-(--admin-text2)" style={{ textAlign: 'right' }}>
                      {a.horas.toFixed(1)}
                    </td>
                  )}
                  {onGenerarActa && (
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        disabled={generandoId === a.asesoriaId}
                        onClick={() => onGenerarActa(a.asesoriaId)}
                        className="admin-btn text-[11px] py-1 px-2.5 disabled:opacity-50"
                      >
                        {generandoId === a.asesoriaId ? 'Generando...' : 'Generar acta'}
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────

export function GestionActasPage() {
  const [sinEvaluacion, setSinEvaluacion] = useState<AsesoriaSinActaDto[]>([]);
  const [sinActa, setSinActa] = useState<AsesoriaSinActaDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generandoId, setGenerandoId] = useState<number | null>(null);
  const [generarError, setGenerarError] = useState<string | null>(null);

  const cargar = () => {
    setIsLoading(true);
    setError(null);
    Promise.all([sysadminService.obtenerAsesoriasSinEvaluacion(), sysadminService.obtenerAsesoriasSinActa()])
      .then(([resSinEval, resSinActa]) => {
        setSinEvaluacion(resSinEval.data);
        setSinActa(resSinActa.data);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar'))
      .finally(() => setIsLoading(false));
  };

  useEffect(cargar, []);

  const handleGenerarActa = async (asesoriaId: number) => {
    setGenerandoId(asesoriaId);
    setGenerarError(null);
    try {
      const res = await sysadminService.generarActaFaltante(asesoriaId);
      if (res.success) {
        cargar();
      } else {
        setGenerarError(res.message || 'Error al generar el acta');
      }
    } catch (err) {
      setGenerarError(err instanceof Error ? err.message : 'Error al generar el acta');
    } finally {
      setGenerandoId(null);
    }
  };

  if (isLoading) return <LoadingContainer text="Cargando actas faltantes..." />;

  if (error) {
    return (
      <div className="admin-card p-4 bg-(--admin-red-bg) border-(--admin-red) text-(--admin-red)">
        <p className="text-[13px] font-medium">Error al cargar: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[18px] font-bold text-(--admin-text) tracking-tight">Actas faltantes</h1>
        <p className="text-[12px] text-(--admin-text3)">
          Asesorías activas que no pueden aprobarse porque les falta el acta final (Evaluacion). Ninguna acción aquí es masiva — el acta se
          genera una por una, ya que asumir contenido para una asesoría que el asesor nunca cerró es una decisión que conviene tomar caso
          por caso.
        </p>
      </div>

      {generarError && (
        <div className="flex items-center gap-2 rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-4 py-3 text-[12px] text-(--admin-red)">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {generarError}
        </div>
      )}

      <div>
        <div className="flex items-center gap-2 mb-2">
          <FileWarning className="w-4 h-4 text-(--admin-purple)" />
          <h2 className="text-[13px] font-semibold text-(--admin-text)">Con evaluación, sin acta (bloqueadas para aprobar)</h2>
        </div>
        <ActasTable
          storageKey="tourixia:sysadmin-actas-sin-acta-cols"
          asesorias={sinActa}
          emptyMessage="No hay asesorías con evaluación pendiente de acta."
          onGenerarActa={handleGenerarActa}
          generandoId={generandoId}
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <ClipboardX className="w-4 h-4 text-(--admin-text3)" />
          <h2 className="text-[13px] font-semibold text-(--admin-text)">Sin ningún requisito ni terna evaluados</h2>
        </div>
        <ActasTable
          storageKey="tourixia:sysadmin-actas-sin-evaluacion-cols"
          asesorias={sinEvaluacion}
          emptyMessage="No hay asesorías sin evaluación registrada."
        />
      </div>
    </div>
  );
}
